import { Suspense } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { PrivateRouter } from "./PrivateRouter";
import { StudentRouter } from "./StudentRouter";
import { InstructorRouter } from "./InstructorRouter";
import { BlobLoader } from "../ui/loader/BlobLoader";
import { LazyLoadErrorBoundary } from "./LazyLoadErrorBoundary";
import { lazyRetry } from "../../utils/lazyRetry";

const DashboardView = lazyRetry(() =>
  import("../../views/DashboardView").then((m) => ({
    default: m.DashboardView,
  })),
);
const CursosView = lazyRetry(() =>
  import("../../views/CursosView").then((m) => ({ default: m.CursosView })),
);
const ProfesoresView = lazyRetry(() =>
  import("../../views/ProfesoresView").then((m) => ({
    default: m.ProfesoresView,
  })),
);
const LoginView = lazyRetry(() =>
  import("../../views/LoginView").then((m) => ({ default: m.LoginView })),
);
const FinanzasView = lazyRetry(() =>
  import("../../views/FinanzasView").then((m) => ({
    default: m.FinanzasView,
  })),
);
const AlumnosView = lazyRetry(() =>
  import("../../views/AlumnosView").then((m) => ({ default: m.AlumnosView })),
);
const ConfigView = lazyRetry(() =>
  import("../../views/ConfigView").then((m) => ({ default: m.ConfigView })),
);
const UsuariosView = lazyRetry(() =>
  import("../../views/UsuariosView").then((m) => ({
    default: m.UsuariosView,
  })),
);
const InicioView = lazyRetry(() =>
  import("../../views/InicioView").then((m) => ({ default: m.InicioView })),
);
const InstructorView = lazyRetry(() =>
  import("../../views/InstructorView").then((m) => ({
    default: m.InstructorView,
  })),
);
const TransfersView = lazyRetry(() =>
  import("../../views/TransfersView").then((m) => ({
    default: m.TransfersView,
  })),
);
const StudentView = lazyRetry(() =>
  import("../../views/StudentView").then((m) => ({ default: m.StudentView })),
);
const TurnView = lazyRetry(() =>
  import("../../views/TurnView").then((m) => ({ default: m.TurnView })),
);
const CajasView = lazyRetry(() =>
  import("../../views/CajasView").then((m) => ({ default: m.CajasView })),
);
const GruposView = lazyRetry(() =>
  import("../../views/GruposView").then((m) => ({
    default: m.GruposView,
  })),
);
const EnrollmentRequestsView = lazyRetry(() =>
  import("../../views/EnrollmentRequestsView").then((m) => ({
    default: m.EnrollmentRequestsView,
  })),
);
const EnrollmentRequestsAdminView = lazyRetry(() =>
  import("../../views/EnrollmentRequestsAdminView").then((m) => ({
    default: m.EnrollmentRequestsAdminView,
  })),
);
const ForgotPasswordView = lazyRetry(() =>
  import("../../views/ForgotPasswordView").then((m) => ({
    default: m.ForgotPasswordView,
  })),
);
const ResetPasswordView = lazyRetry(() =>
  import("../../views/ResetPasswordView").then((m) => ({
    default: m.ResetPasswordView,
  })),
);
// Hidden for now — see the /student route.
// const StudentHome = lazyRetry(() =>
//   import("../layout/student/StudentHome").then((m) => ({
//     default: m.StudentHome,
//   })),
// );
const StudentPaymentReturn = lazyRetry(() =>
  import("../layout/student/StudentPaymentReturn").then((m) => ({
    default: m.StudentPaymentReturn,
  })),
);
const StudentAccount = lazyRetry(() =>
  import("../layout/student/StudentAccount").then((m) => ({
    default: m.StudentAccount,
  })),
);
const CursoData = lazyRetry(() =>
  import("../layout/student/CursoData").then((m) => ({
    default: m.CursoData,
  })),
);

export const AppRouter = () => {
  return (
    <BrowserRouter basename="/">
      <LazyLoadErrorBoundary>
        <Suspense
          fallback={
            <div className="suspense-wrapper">
              <BlobLoader />
            </div>
          }
        >
          <Routes>
            <Route
              path="/*"
              element={
                <PrivateRouter>
                  <InicioView />
                </PrivateRouter>
              }
            />
            <Route path="/login" element={<LoginView />} />
            <Route path="/forgot-password" element={<ForgotPasswordView />} />
            <Route path="/reset-password" element={<ResetPasswordView />} />
            <Route path="/inscripciones" element={<EnrollmentRequestsView />} />
            <Route
              path="/estadisticas"
              element={
                <PrivateRouter seccion="stats">
                  <DashboardView />
                </PrivateRouter>
              }
            />
            <Route
              path="/alumnos"
              element={
                <PrivateRouter seccion="students">
                  <AlumnosView />
                </PrivateRouter>
              }
            />
            <Route
              path="/inscripciones/admin"
              element={
                <PrivateRouter seccion="students">
                  <EnrollmentRequestsAdminView />
                </PrivateRouter>
              }
            />
            <Route
              path="/instructores"
              element={
                <PrivateRouter seccion="instructors">
                  <ProfesoresView />
                </PrivateRouter>
              }
            />
            <Route
              path="/cursos"
              element={
                <PrivateRouter seccion="courses">
                  <CursosView />
                </PrivateRouter>
              }
            />
            <Route
              path="/grupos"
              element={
                <PrivateRouter seccion="courses">
                  <GruposView />
                </PrivateRouter>
              }
            />
            <Route
              path="/finanzas"
              element={
                <PrivateRouter seccion="moves">
                  <FinanzasView />
                </PrivateRouter>
              }
            />
            <Route
              path="/finanzas/cajas"
              element={
                <PrivateRouter seccion="settings">
                  <CajasView />
                </PrivateRouter>
              }
            />
            <Route
              path="/transferencias"
              element={
                <PrivateRouter seccion="transfers">
                  <TransfersView />
                </PrivateRouter>
              }
            />
            <Route
              path="/configuracion"
              element={
                <PrivateRouter seccion="settings">
                  <ConfigView />
                </PrivateRouter>
              }
            />
            <Route
              path="/usuarios"
              element={
                <PrivateRouter seccion="users">
                  <UsuariosView />
                </PrivateRouter>
              }
            />
            <Route
              path="/student"
              element={
                <StudentRouter>
                  <StudentView>
                    {/* Mi cuenta is the portal's home; "Mis cursos"
                        (StudentHome) is hidden until it shows something. */}
                    <StudentAccount />
                  </StudentView>
                </StudentRouter>
              }
            />
            <Route
              path="/student/curso"
              element={
                <StudentRouter>
                  <StudentView>
                    <CursoData />
                  </StudentView>
                </StudentRouter>
              }
            />
            {/* Back from MercadoPago: exito | error | pendiente */}
            <Route
              path="/student/pago/:result"
              element={
                <StudentRouter>
                  <StudentView>
                    <StudentPaymentReturn />
                  </StudentView>
                </StudentRouter>
              }
            />
            <Route
              path="/student/cuenta"
              element={
                <StudentRouter>
                  <StudentView>
                    <StudentAccount />
                  </StudentView>
                </StudentRouter>
              }
            />
            <Route
              path="/instructor"
              element={
                <InstructorRouter>
                  <InstructorView />
                </InstructorRouter>
              }
            />

            <Route
              path="/turno"
              element={
                <PrivateRouter>
                  <TurnView />
                </PrivateRouter>
              }
            />
          </Routes>
        </Suspense>
      </LazyLoadErrorBoundary>
    </BrowserRouter>
  );
};
